import { ReplaySubject, Observable } from "rxjs";
import {
    LoggingService, TokenService, LocalStorageService,
    NotificationsService, PlatformService, Confirmation,
    UserUpdateInfo, User
} from "@varsity/services";
import { OnInit, OnDestroy, OnChanges, SimpleChanges } from "@angular/core";
import { UrlConstants } from "@varsity/commons";
import { ToastController, PopoverController, Popover } from "ionic-angular";
import {
    DisplayMessageParams, ToasterConfig, LoginComponent, RecoverPasswordComponent, DisplayMessageComponent
} from "@varsity/components"
import { InAppBrowser } from '@ionic-native/in-app-browser';

// https://angular.io/guide/lifecycle-hooks
export abstract class BaseComponent implements OnInit, OnDestroy, OnChanges {
    private static instanceID: number = 0;
    protected isWebApp: boolean;
    protected platformDevice: string;
    protected platformType: string;

    protected instance: number;
    componentDestroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1)

    protected showSpinner: boolean = false;
    protected basePath: string = '';
    protected displayNameLimit: number = 5;

    constructor(
        protected loggingService: LoggingService,
        protected tokenService: TokenService,
        protected localStorageService: LocalStorageService,
        protected notificationsService: NotificationsService,
        protected platformService: PlatformService,
        protected toastCtrl: ToastController,
        protected popoverCtrl: PopoverController,
        protected iab: InAppBrowser,
        protected component: string,
  
    ) {

        BaseComponent.instanceID++;
        this.instance = BaseComponent.instanceID;
    }

    // TODO showNotification refactoring - in progress
    //protected showNotification(message: string, duration: number = 7500, hasIcon?: boolean, config?: ToasterConfig) {
    //    let toast = this.toastCtrl.create({
    //        message: ((hasIcon ? "<ion-icon name='" + config.iconName + "'></ion-icon>" : "")) + message,
    //        duration: duration,
    //        position: 'bottom',
    //        showCloseButton: true,
    //        dismissOnPageChange: true,
    //        cssClass: config.cssClass
    //    });

    //    toast.present();
    //}

    protected showNotification(message: string, duration: number = 7500, dismissOnPageChange: boolean = true, cssClass: string = "default-toast") {
        let toast = this.toastCtrl.create({
            message: message,
            duration: duration,
            position: 'bottom',
            showCloseButton: true,
            dismissOnPageChange: dismissOnPageChange,
            cssClass: cssClass
        });

        toast.present();
    }

    protected isUserLoggedIn(): boolean {
        return this.localStorageService.get('user') != null;
    }

    protected getUser(): User {
        return JSON.parse(this.localStorageService.get('user')) as User;
    }

    protected handleError(error) {
        if (error && error._body) {
            this.showNotification(error._body, undefined, undefined, "error");
        } else {
            this.showNotification(error, undefined, undefined, "error");
        }
    }


    protected getUserDisplayName(userObject: UserUpdateInfo | User): string {
        var name = "";
        if ((userObject.FirstName + userObject.LastName).length > this.displayNameLimit) {
            name = userObject.FirstName;
        } else {
            name = userObject.FirstName + " " + userObject.LastName;
        }
        return name || (userObject as User).UserID;
    }

    //protected showCartIcon(): boolean {
    //    return this.localStorageService.get('confirmation') != null;
    //}

    protected getCart(): Confirmation {
        if (this.localStorageService.get('confirmation') != null) {
            return JSON.parse(this.localStorageService.get('confirmation'));
        }

    }

    protected hideKeyboard(event): void {
        if (event.keyCode == 13) {
            let activeElement = <HTMLElement>document.activeElement;
            activeElement && activeElement.blur && activeElement.blur();
        }
    }

    ngOnChanges(changes: SimpleChanges): void {

    }

    ngOnDestroy(): void {
        this.loggingService.debug(`ngOnDestroy: ${this.component} ${this.instance}`);

        this.componentDestroyed$.next(true);
        this.componentDestroyed$.complete();
    }

    ngOnInit(): void {
        this.loggingService.debug(`ngOnInit: ${this.component} ${this.instance}`);

        this.showSpinner = this.notificationsService.runningRequestsCount > 0;
        this.notificationsService.runningRequestsCount$
            .takeUntil(this.componentDestroyed$)
            .subscribe((value) => {
                this.showSpinner = this.notificationsService.runningRequestsCount > 0;
            });

        this.basePath = UrlConstants.teamLogoPath;

        this.isWebApp = this.platformService.isWebApp;
        this.platformDevice = this.platformService.platformDevice;
        this.platformType = this.platformService.platformType;

        this.platformService.webAppFlag$
            .takeUntil(this.componentDestroyed$)
            .subscribe(
                (value) => {
                    this.isWebApp = value;
            })

        this.platformService.platformDevice$
            .takeUntil(this.componentDestroyed$)
            .subscribe(
                (value) => {
                    this.platformDevice = value;
                });

        this.platformService.platformType$
            .takeUntil(this.componentDestroyed$)
            .subscribe(
                (value) => {
                    this.platformType = value;
                });
    }

    protected includeBaseBath(path: string): string {
        return this.basePath + path;
    }

    protected showPopover(component: any, data: DisplayMessageParams, cssClass: string, onDismiss: (data: any) => void, forceCloseTimer: number = 0) {
        // show a login popup
        let p = this.popoverCtrl.create(component, { params: data }, {
            enableBackdropDismiss: false,
            cssClass: cssClass
        });

        p.present();

        let dismissed: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);
        p.onWillDismiss((data) => {
            /* the object from here gets passed to this onDismiss()
             * this.viewCtrl.dismiss({
                recover: recover,
                email: this.userId
            });*/

            dismissed.next(true);
            dismissed.complete();
            onDismiss(data);
        });

        if (forceCloseTimer && forceCloseTimer > 0) {
            let observable = Observable.timer(forceCloseTimer);
            observable
                .takeUntil(dismissed)
                .subscribe(() => {
                    p.dismiss();
            });
        }          
    }

    openLoginPopover(component: string, data = {}, popoverClosed = () => { }) {
        let p: Popover;
        switch (component) {
            case 'login': {
                p = this.popoverCtrl.create(LoginComponent, data, {
                    enableBackdropDismiss: false,
                    cssClass: 'login-popover'
                });

                p.onDidDismiss((data, role) => {
                    if (data && data.recover) {
                        this.openLoginPopover('recover-password', data, popoverClosed);
                    } else {
                        //put update version pop-over verification here
                        this.openUpdateVersionPopOver(data, popoverClosed);
                    }
                });

                break;
            }

            case 'recover-password': {
                p = this.popoverCtrl.create(RecoverPasswordComponent, data, {
                    enableBackdropDismiss: false,
                    cssClass: 'recover-password-popover'
                });

                p.onDidDismiss((data, role) => {
                    if (data && data.redirectToLogin) {
                        this.openLoginPopover('login', data, popoverClosed);
                    } else {
                        this.openUpdateVersionPopOver(data, popoverClosed);
                    }
                });
            }
            default:
        }

        if (p) { p.present(); }
    }

    openUpdateVersionPopOver(data, popoverClosed) {
        if (data.updateRequired && !this.isWebApp) {
            this.showPopover(DisplayMessageComponent, {
                icon: "update",
                header: "Update version",
                content: [data.text],
                buttons: [{ tag: 'Update', text: 'Update' }, { tag: 'Close', text: 'Close' }],
                footer: null,
            }, "update-version-popover", (data) => {
                let redirectURL = "";
                if (data.button && data.button.tag == 'Update') {
                    if (this.platformType == "android") {
                        redirectURL = UrlConstants.googlePlayUrl;
                    } else if (this.platformType == "ios") {
                        redirectURL = UrlConstants.appStoreUrl;
                    }
                    this.iab.create(redirectURL, "_system");
                } else {
                    popoverClosed();
                }
            })
        } else {
            popoverClosed();
        }
    }
}



// WEBPACK FOOTER //
// ./src/components/BaseComponent.ts